import {
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from "@ionic/react";
import "driver.js/dist/driver.css";
import { trendingUpOutline } from "ionicons/icons";
import React, { Dispatch, useEffect, useMemo, useState } from "react";
import { Route, RouteProps, useLocation } from "react-router-dom";
import { CMEDRoutes } from "./Constants";
import { AccessTokenContextType } from "../../components/contexts/AccessTokenContext";
import { EntitlementContextType } from "../../components/contexts/EntitlementContext";
import { pubsubEvents } from "../../components/helpers/socket/pubsubEvent";
import AppAuth from "../../pages/AppAuth";
import LogoutSuccess from "../../pages/LogoutSuccess";
import MobileAppAuth from "../../pages/MobileAppAuth";
import ProtectedRouteCMED from "../../routing/ProtectedRouteCMED";
import MDIcon from "../../theme/custom-icons/MD.svg";
import OBIcon from "../../theme/custom-icons/OB.svg";
import OGIcon from "../../theme/custom-icons/OG.svg";
import MoreIcon from "../../theme/custom-icons/More.svg";
import OneOverlayLoader from "../../utils/component-utils/OverlayLoader/OneOverlayLoader";
import MarketData from "../cmed/MarketData/MarketData";
import { CMEDPermissions } from "../cmed/MarketData/MarketDataContent";
import OptionsGridSuperWrapper from "../cmed/OptionsGrid/OptionsGridSuperWrapper";
import OrderBook from "../cmed/OrderBook/OrderBook";
import NoAccess from "../../pages/NoAccess";
import Positions from "../cmed/Positions/Positions";
import MorePage from "../../pages/MorePage";
import { hapticsImpactMedium } from "../../utils/core-utils/hapticUtils";
import firebaseEventConfig from "../../utils/firebaseAnalytics/firebaseEventConfig";
import { CMEDfirebaseLogEvent } from "../../utils/firebaseAnalytics/firebaseUtils";

/**
 * @author
 * E45597 Aryamitra Chaudhuri
 */

interface PlatformsSwitcherProps extends RouteProps {
  entitlements: EntitlementContextType | undefined;
  accessToken: AccessTokenContextType | undefined;
  accessTokenfetched: Function;
  isUserTermsConditionAccepted: any;
  setIsUserTermsConditionAccepted: any;
  loginResponseStatus: any;
  setSendRequest: Dispatch<any>;
  sendRequest: any[];
  noCMEDAccess: boolean;
  onLogin: any;
  setIsCMEDProfileSelectionCompleted: any;
  isUserSettingsModalOpen: any;
  persistedOptionGrids: any;
  setPersistedOptionGrids: any;
}

const PlatformsSwitcher: React.FC<PlatformsSwitcherProps> = ({
  entitlements,
  accessToken,
  accessTokenfetched,
  isUserTermsConditionAccepted,
  setIsUserTermsConditionAccepted,
  loginResponseStatus,
  setSendRequest,
  sendRequest,
  noCMEDAccess,
  onLogin,
  setIsCMEDProfileSelectionCompleted,
  isUserSettingsModalOpen,
  persistedOptionGrids,
  setPersistedOptionGrids,
}) => {
  const [isCMEDLoggedIn, setisCMEDLoggedIn] = useState(false);
  const [userCMEDEntitlements, setuserCMEDEntitlements] =
    useState<CMEDPermissions>();
  const [showAuthLoader, setShowAuthLoader] = useState(false);
  const [activeTab, setActiveTab] = useState("Market");
  const [currentSelectedProfile, setCurrentSelectedProfile] = useState<
    number | undefined
  >(undefined);

  const location = useLocation();

  const handleTabClick = (tab: string) => {
    if (activeTab !== tab) {
      hapticsImpactMedium();
      switch (tab) {
        case "Positions":
          CMEDfirebaseLogEvent(
            firebaseEventConfig.CMEDComponentNames.CMED_POSITIONS,
            firebaseEventConfig.eventNames.CMED_POSITIONS
              .CMED_POSITIONS_VIEW_WINDOW,
          );
          break;
        case "Orders":
          CMEDfirebaseLogEvent(
            firebaseEventConfig.CMEDComponentNames.CMED_ORDER_BOOK,
            firebaseEventConfig.eventNames.CMED_ORDER_BOOK
              .CMED_ORDER_BOOK_VIEW_WINDOW,
          );
          break;
        case "Options":
          CMEDfirebaseLogEvent(
            firebaseEventConfig.CMEDComponentNames.CMED_OPTIONS_GRID,
            firebaseEventConfig.eventNames.CMED_OPTIONS_GRID
              .CMED_OPTIONS_GRID_VIEW_WINDOW,
          );
          break;
        default:
          break;
      }
    }
    setActiveTab(tab);
  };

  async function updateCMEDUserEntitlements(data: any, res: any) {
    const payload = res.payload.data[0];

    const permissions: CMEDPermissions = {
      canKill: payload.cc,
      canHold: payload.ch,
      canAmend: payload.cm,
      canSubmit: payload.cs,
      warnWhenModifyingOrder: payload.w,
      warnWhenSubmittingOrder: payload.ww,
      companyName: payload.c,
      fullName: payload.f,
      type: payload.t,
      accountName: payload.s,
      availableGreeksSources: payload.a,
      defaultOrderHandling: payload.d,
      showOrderHandling: payload.sc,
      showOnBehalfOf: payload.so ? payload.so : false,
      agressiveOrderIndicator: payload.ua,
      showNotes: payload.son,
    };
    setuserCMEDEntitlements(permissions);
  }

  var getLogonResponse = function (msg: any, data: any) {
    setisCMEDLoggedIn(data?.payload?.isLoggedIn);
    if (data?.payload?.isLoggedIn === true) {
      setIsCMEDProfileSelectionCompleted(false);
      setCurrentSelectedProfile(data?.payload?.loginSelection?.id);
    }
  };

  var loginSelectionNeeded = function (msg: any, data: any) {
    setisCMEDLoggedIn(false);
  };

  useEffect(() => {
    PubSub.subscribe(
      pubsubEvents.stompSubscriptionKeys.CMED_USER_LOGON_SUCCESS,
      getLogonResponse,
    );

    PubSub.subscribe(
      pubsubEvents.stompSubscriptionKeys.CMED_LOGIN_SELECTION_NEEDED,
      loginSelectionNeeded,
    );

    PubSub.subscribe(
      pubsubEvents.stompSubscriptionKeys.CMED_ORDER_USER_DETAILS_UPDATE,
      (data, res) => {
        updateCMEDUserEntitlements(data, res);
      },
    );
  }, []);

  useEffect(() => {
    if (location.pathname === "/auth" || location.pathname === "/") {
      setShowAuthLoader(true);
    } else {
      setShowAuthLoader(false);
    }
  }, [location]);

  return (
    <>
      {showAuthLoader && (
        <OneOverlayLoader
          loadingText={["Authenticating..."]}
          shouldDisplayLogoAndSlogan={true}
        />
      )}
      <IonTabs>
        <IonRouterOutlet>
          {/* <=====% CMED tabs %=====>*/}
          <Route exact path={CMEDRoutes.MARKET_DATA}>
            <ProtectedRouteCMED
              onLogin={onLogin}
              isCMEDLoggedIn={isCMEDLoggedIn}
              isUserTermsConditionAccepted={isUserTermsConditionAccepted}
              setIsUserTermsConditionAccepted={setIsUserTermsConditionAccepted}
              accessToken={accessToken}
              loginResponseStatus={loginResponseStatus}
              noCMEDAccess={noCMEDAccess}
              setIsCMEDProfileSelectionCompleted={
                setIsCMEDProfileSelectionCompleted
              }
            >
              <MarketData
                isCMEDLoggedIn={isCMEDLoggedIn}
                userCMEDEntitlements={userCMEDEntitlements}
                setSendRequest={setSendRequest}
                sendRequest={sendRequest}
                isUserSettingsModalOpen={isUserSettingsModalOpen}
                currentSelectedProfile={currentSelectedProfile}
              />
            </ProtectedRouteCMED>
          </Route>
          <Route exact path="/cmed/options-grid">
            <ProtectedRouteCMED
              onLogin={onLogin}
              isCMEDLoggedIn={isCMEDLoggedIn}
              isUserTermsConditionAccepted={isUserTermsConditionAccepted}
              setIsUserTermsConditionAccepted={setIsUserTermsConditionAccepted}
              accessToken={accessToken}
              loginResponseStatus={loginResponseStatus}
              noCMEDAccess={noCMEDAccess}
            >
              <OptionsGridSuperWrapper
                userCMEDEntitlements={userCMEDEntitlements}
                isUserSettingsModalOpen={isUserSettingsModalOpen}
                persistedOptionGrids={persistedOptionGrids}
                setPersistedOptionGrids={setPersistedOptionGrids}
              />
            </ProtectedRouteCMED>
          </Route>
          <Route exact path={CMEDRoutes.ORDER_BOOK}>
            <ProtectedRouteCMED
              onLogin={onLogin}
              isCMEDLoggedIn={isCMEDLoggedIn}
              isUserTermsConditionAccepted={isUserTermsConditionAccepted}
              setIsUserTermsConditionAccepted={setIsUserTermsConditionAccepted}
              accessToken={accessToken}
              loginResponseStatus={loginResponseStatus}
              noCMEDAccess={noCMEDAccess}
            >
              <OrderBook
                isUserSettingsModalOpen={isUserSettingsModalOpen}
                userCMEDEntitlements={userCMEDEntitlements}
              />
            </ProtectedRouteCMED>
          </Route>
          <Route exact path="/cmed/positions">
            <ProtectedRouteCMED
              onLogin={onLogin}
              isCMEDLoggedIn={isCMEDLoggedIn}
              isUserTermsConditionAccepted={isUserTermsConditionAccepted}
              setIsUserTermsConditionAccepted={setIsUserTermsConditionAccepted}
              accessToken={accessToken}
              loginResponseStatus={loginResponseStatus}
              noCMEDAccess={noCMEDAccess}
            >
              <Positions />
            </ProtectedRouteCMED>
          </Route>
          <Route exact path="/cmed/more">
            <ProtectedRouteCMED
              onLogin={onLogin}
              isCMEDLoggedIn={isCMEDLoggedIn}
              isUserTermsConditionAccepted={isUserTermsConditionAccepted}
              setIsUserTermsConditionAccepted={setIsUserTermsConditionAccepted}
              accessToken={accessToken}
              loginResponseStatus={loginResponseStatus}
              noCMEDAccess={noCMEDAccess}
            >
              <MorePage currentSelectedProfile={currentSelectedProfile} />
            </ProtectedRouteCMED>
          </Route>
          <Route path="/auth">
            <AppAuth accessTokenfetched={accessTokenfetched} />
          </Route>
          <Route path="/mobileAuth">
            <MobileAppAuth accessTokenfetched={accessTokenfetched} />
          </Route>
          <Route exact path="/logout_success">
            <LogoutSuccess onLogin={onLogin} />
          </Route>
          <Route exact path="/noaccess">
            <NoAccess />
          </Route>
        </IonRouterOutlet>
        <IonTabBar
          slot="bottom"
          style={{
            display:
              accessToken?.valid &&
              isUserTermsConditionAccepted &&
              entitlements?.entitlements?.includes("CMED")
                ? ""
                : "none",
          }}
        >
          <IonTabButton
            disabled={!isCMEDLoggedIn}
            tab="Market"
            href={CMEDRoutes.MARKET_DATA}
            onClick={() => handleTabClick("Market")}
          >
            <IonIcon
              src={MDIcon}
              className={activeTab === "Market" ? "pulsing" : ""}
            />
            <IonLabel>Market</IonLabel>
            <div className="tab-indicator"></div>
          </IonTabButton>
          <IonTabButton
            disabled={!isCMEDLoggedIn}
            tab="OptionsGrid"
            href="/cmed/options-grid"
            onClick={() => handleTabClick("Options")}
          >
            <IonIcon
              src={OGIcon}
              className={activeTab === "Options" ? "pulsing" : ""}
            />
            <IonLabel>Options</IonLabel>
            <div className="tab-indicator"></div>
          </IonTabButton>
          <IonTabButton
            disabled={!isCMEDLoggedIn}
            tab="Orders"
            href={CMEDRoutes.ORDER_BOOK}
            onClick={() => handleTabClick("Orders")}
          >
            <IonIcon
              src={OBIcon}
              className={activeTab === "Orders" ? "pulsing" : ""}
            />
            <IonLabel>Orders</IonLabel>
            <div className="tab-indicator"></div>
          </IonTabButton>
          <IonTabButton
            disabled={!isCMEDLoggedIn}
            tab="Positions"
            href="/cmed/positions"
            onClick={() => handleTabClick("Positions")}
          >
            <IonIcon
              icon={trendingUpOutline}
              className={activeTab === "Positions" ? "pulsing" : ""}
            />
            <IonLabel>Positions</IonLabel>
            <div className="tab-indicator"></div>
          </IonTabButton>
          <IonTabButton
            disabled={!isCMEDLoggedIn}
            tab="More"
            href="/cmed/more"
            onClick={() => handleTabClick("More")}
          >
            <IonIcon
              icon={MoreIcon}
              className={activeTab === "More" ? "pulsing" : ""}
            />
            <IonLabel>More</IonLabel>
            <div className="tab-indicator"></div>
          </IonTabButton>
        </IonTabBar>
      </IonTabs>
    </>
  );
};
export default PlatformsSwitcher;
